import { useEffect, useId, useRef, useState } from 'react'
import type { GuidedSaveOptions } from '@/components/grid/commitOpenEditor'
import {
  useGuidedAutosaveReporter,
  useGuidedPendingReporter,
} from '@/features/config/contexts/guided-edit-context'

/**
 * SCRUM-1032 — writes a guided-setup grid's staged rows once the user pauses.
 *
 * The wizard already flushes every grid on Next, but a step can be a long sit:
 * a controller with a thousand decoders, or a pipe tree entered branch by
 * branch. Leaving all of that unwritten until the user happens to move on makes
 * a closed tab expensive. Saving on a pause costs one request per burst of
 * typing and keeps the gap between "typed" and "stored" measured in seconds.
 *
 * Only inside the wizard: on /config the Save button is the contract, and a
 * grid that wrote by itself would be a surprise there.
 */

/** Long enough to cover the gap between two fields, short enough to feel live. */
export const GUIDED_AUTOSAVE_DELAY_MS = 2500

/**
 * How often the held-back save re-asks whether the editor has closed. The user
 * is typing, so this costs nothing but a boolean read.
 */
export const GUIDED_AUTOSAVE_EDIT_RECHECK_MS = 700

/**
 * How long an open editor may hold the save before it goes anyway.
 *
 * SCRUM-1032 QC, 5 Oct — committing a cell with Enter opens the next one down,
 * which is the spreadsheet behaviour people expect but leaves the grid
 * "editing" for as long as the user leaves it there. The guard below then held
 * the write indefinitely: a decoder renamed and left alone sat unsaved with
 * nothing on screen to say so, and only went in when something else happened to
 * close the editor. Reproduced on D2 on 6 Oct — the name landed the instant
 * Escape was pressed, eighteen seconds after the edit.
 *
 * The save commits whatever editor is open before it reads the staged rows, so
 * going ahead cannot lose a value in progress.
 */
export const GUIDED_AUTOSAVE_MAX_EDIT_HOLD_MS = 6_000

/**
 * SCRUM-1032 QC round 7 — whether a save that resolved still failed.
 *
 * The guided grids do not throw on a refused write: they resolve
 * `{ failed, total }` and leave the failed rows staged. Reading only the
 * promise reported every one of those as 'done', so the step showed "All
 * changes saved" beside the grid's own error toast (Wei Woon, 21 Oct).
 */
export const savedWithFailures = (result: unknown): boolean =>
  typeof result === 'object' &&
  result !== null &&
  Number((result as { failed?: unknown }).failed) > 0

interface UseGuidedAutosaveArgs {
  /** Guided setup, in bulk edit, and allowed to write. */
  enabled: boolean
  /** Anything staged to write. Nothing pending, nothing scheduled. */
  hasPendingChanges: boolean
  /**
   * Whether leaving the page would lose what is staged; drives the browser's
   * "Leave site?" prompt. Defaults to `hasPendingChanges`. A grid that keeps
   * its unwritten rows elsewhere (Station's drafts live in localStorage) says
   * less, or the prompt fires on every visit with an unfinished row.
   */
  losesOnLeave?: boolean
  /**
   * Changes on every edit, including edits held in a ref the effect cannot
   * otherwise see. Each change restarts the timer, so a burst of typing is one
   * save rather than one per keystroke.
   */
  version: number
  /**
   * Called with no argument, except `{ leaveEditorOpen: true }` for a grid
   * that sets `keepsEditorOpen`. A save with options of its own (Controllers'
   * `{ force }`) never receives them from here.
   */
  save:
    | (() => void | Promise<unknown>)
    | ((options?: GuidedSaveOptions) => void | Promise<unknown>)
  /**
   * SCRUM-1032 QC, 30 Oct — when an editor outlasts the hold, the save is
   * asked to skip that row and leave its editor open, instead of closing it.
   * The held row is written once the editor closes. For grids whose save
   * honours `leaveEditorOpen` (ET Reference, Plants, Soil, Emitter, Areas).
   * Without it the save closes the editor as before.
   */
  keepsEditorOpen?: boolean
  /**
   * True while a cell editor is open. Typing inside one moves no version — the
   * value is not staged until the editor commits — so the timer would run out
   * mid-entry, and every save starts by closing the open editor. The wait is
   * held for as long as the user is in a cell.
   */
  isEditing?: () => boolean
  /**
   * True while a save is already running. The write is held rather than
   * queued: a second run started mid-save reads the same staged rows the first
   * one has not finished writing, and creates them a second time.
   */
  isBusy?: () => boolean
  /**
   * The grid shows its own save status, so the wizard should not show a second
   * one over it. True for the decoder and station lists, which report a live
   * row count in their own toolbars — richer than the wizard's one line, and
   * saying the same thing twice reads as two different things happening.
   */
  reportsOwnState?: boolean
}

const useGuidedAutosave = ({
  enabled,
  hasPendingChanges,
  losesOnLeave = hasPendingChanges,
  version,
  save,
  keepsEditorOpen = false,
  isEditing,
  isBusy,
  reportsOwnState = false,
}: UseGuidedAutosaveArgs) => {
  // SCRUM-1032 — the wizard shows one status for the step, so the grids do not
  // each raise a success toast. Read here rather than passed by every caller:
  // this hook is the only thing that starts a background write.
  const contextReport = useGuidedAutosaveReporter()
  const report = reportsOwnState ? undefined : contextReport
  const reportRef = useRef(report)
  reportRef.current = report
  const reportPending = useGuidedPendingReporter()
  const reportPendingRef = useRef(reportPending)
  reportPendingRef.current = reportPending
  // Which grid this is, so the wizard can end a failure with the grid that
  // caused it.
  const source = useId()

  // Read through a ref: a new closure each render must not restart the timer.
  const isEditingRef = useRef(isEditing)
  isEditingRef.current = isEditing
  const isBusyRef = useRef(isBusy)
  isBusyRef.current = isBusy
  /**
   * The save too. A grid that passes a new closure each render (the Station
   * list passes `() => runSerialised(runAutosave)`) restarted the timer on
   * every render while a refused edit stayed staged — its own progress and
   * error updates re-armed it, and the same PUT went out every ~2.6 s until
   * the server took it (SCRUM-1032 round 7 follow-up, D2). Only `version`,
   * moved by user edits and the grids' own bounded retries, re-arms it now.
   */
  const saveRef = useRef(save)
  saveRef.current = save
  const keepsEditorOpenRef = useRef(keepsEditorOpen)
  keepsEditorOpenRef.current = keepsEditorOpen

  /**
   * SCRUM-1032 QC round 7 follow-up — a clean write is only "saved" if it
   * left nothing behind.
   *
   * A plant added while the write was out stays staged for the next pass,
   * and the step still said "All changes saved". The answer waits for the
   * render that carries the save's own state updates; the tick is set in the
   * same batch, so that render sees what is left.
   */
  const mountedRef = useRef(true)
  const [cleanSaveTick, setCleanSaveTick] = useState(0)
  /**
   * Numbers each run. A result is reported only while its run is the latest:
   * the next run can start in the gap before this render, and an older
   * 'done' landing after its 'start' would end its "Saving…" early.
   */
  const latestRunRef = useRef(0)
  const cleanSaveSettlingRef = useRef(0)
  useEffect(() => {
    const run = cleanSaveSettlingRef.current
    if (!run) return
    cleanSaveSettlingRef.current = 0
    if (run !== latestRunRef.current) return
    reportRef.current?.(hasPendingChanges ? 'held' : 'done', source)
    // Only the tick: this answers one finished save, not every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanSaveTick])

  useEffect(() => {
    if (!enabled || !hasPendingChanges) return

    let timeoutId = 0
    let heldSince = 0
    // The editor outlasted the hold and the other rows have been sent; what
    // is left waits for that editor to close.
    let sentAroundEditor = false

    const recheck = () => {
      timeoutId = window.setTimeout(runOrWait, GUIDED_AUTOSAVE_EDIT_RECHECK_MS)
    }

    const runSave = (options?: GuidedSaveOptions) => {
      // Awaited so the status can say when the write finished, not only that
      // one started. `save` is serialised per grid, so this cannot overlap.
      latestRunRef.current += 1
      const run = latestRunRef.current
      reportRef.current?.('start', source)
      const save = saveRef.current as (
        options?: GuidedSaveOptions,
      ) => void | Promise<unknown>
      Promise.resolve(options ? save(options) : save())
        .then((result) => {
          // Gone mid-write: 'leave' already ended this write for the wizard,
          // and an answer now would pin it on a grid that is not there.
          // Overtaken: the newer run reports for both.
          if (!mountedRef.current || run !== latestRunRef.current) return
          if (savedWithFailures(result)) {
            reportRef.current?.('error', source)
            return
          }
          // Decided after the grid re-renders with what the save left
          // staged: read now, `hasPendingChanges` is still the value from
          // before the save.
          cleanSaveSettlingRef.current = run
          setCleanSaveTick((tick) => tick + 1)
        })
        .catch(() => {
          if (mountedRef.current && run === latestRunRef.current)
            reportRef.current?.('error', source)
        })
    }

    const runOrWait = () => {
      const busy = isBusyRef.current?.()
      const editing = isEditingRef.current?.()
      if (busy || editing) {
        // A busy grid is told to wait for as long as it takes — it is writing.
        // An open editor is only allowed to hold the save for a while: see
        // GUIDED_AUTOSAVE_MAX_EDIT_HOLD_MS.
        const now = Date.now()
        if (!heldSince) heldSince = now
        if (busy || now - heldSince < GUIDED_AUTOSAVE_MAX_EDIT_HOLD_MS) {
          recheck()
          return
        }
        if (keepsEditorOpenRef.current) {
          // Everything but the row being edited, once; then wait for the
          // editor to close and save that row too.
          if (!sentAroundEditor) {
            sentAroundEditor = true
            runSave({ leaveEditorOpen: true })
          }
          recheck()
          return
        }
      }
      runSave()
    }

    timeoutId = window.setTimeout(runOrWait, GUIDED_AUTOSAVE_DELAY_MS)

    return () => window.clearTimeout(timeoutId)
    // `version` is the dependency that matters: it is what moves when the edit
    // buffers behind `hasPendingChanges` are mutated in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hasPendingChanges, version])

  /**
   * Nothing left to save means nothing left refused: a refused row the user
   * deletes or discards never saves again, so its "Could not save" would
   * stand with nothing behind it.
   *
   * Pending changes only, not `enabled`. Several grids switch the autosave
   * off while a blank new row is being typed (`draftsReadyToSave`); reading
   * that as settled dropped a refusal that was still staged, and the next
   * clean save elsewhere then claimed "All changes saved". Unmounting ends
   * it through 'leave' below.
   */
  useEffect(() => {
    if (!hasPendingChanges) reportRef.current?.('clear', source)
  }, [hasPendingChanges, source])
  useEffect(() => {
    // Whatever `enabled` says: a blank draft held back from autosave is still
    // lost if the browser leaves.
    reportPendingRef.current?.(source, losesOnLeave)
  }, [losesOnLeave, source])
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      reportRef.current?.('leave', source)
      reportPendingRef.current?.(source, false)
    }
  }, [source])
}

export default useGuidedAutosave
